<template>
    <!-- ID: js_guide-popover 在Onbo.vue 中有调用 -->
    <div
        :class="[$style.guide, guidePopoverVisible ? $style.show : $style.hide]"
        id="js_guide-popover"
    >
        <template v-if="guidePopoverVisible">
            <pt-icon icon="pt-icon-close" :class="$style.close" @click="handleCloseClick"></pt-icon>

            <header v-if="!visibleComplete">
                <h2>{{ $t('guide.guide_popover_title') }}</h2>
                <p>{{ $t('guide.guide_popover_desc') }}</p>
            </header>

            <!-- 完成提示框 -->
            <GuideComplete v-if="visibleComplete" @close="emit('close')"></GuideComplete>

            <!-- 步骤列表 -->
            <div :class="$style.container" v-else>
                <pt-scrollbar ref="guideScroll">
                    <pt-collapse data-label="collapse" accordion v-model="activeCategoryKey">
                        <pt-collapse-item
                            v-for="(category, $itemIndex) in categories"
                            :key="$itemIndex"
                            :name="category.key"
                            :data-percentage="getStepProgress(category)"
                        >
                            <template #title>
                                <span>{{ $t(category.title) }}</span>
                                <pt-progress
                                    color="#29B6F6"
                                    :stroke-width="2"
                                    :show-text="false"
                                    :percentage="getStepProgress(category)"
                                />
                            </template>

                            <div :class="$style.step">
                                <pt-collapse
                                    accordion
                                    v-model="category.activeStepKey"
                                    v-if="category.steps"
                                >
                                    <pt-collapse-item
                                        v-for="(step, $stepIndex) in category.steps"
                                        :data-done="step.done"
                                        :key="$stepIndex"
                                        :name="step.key"
                                    >
                                        <template #title>
                                            <div :class="[$style.order, step.done && $style.done]">
                                                <pt-icon icon="pt-icon-solidtick"></pt-icon>
                                                <span>{{ $stepIndex + 1 }}</span>
                                            </div>

                                            <h3>{{ $t(step.title) }}</h3>
                                        </template>

                                        <!--TODO：暂去除成功提示-->
                                        <!--
                                        <div :class="$style.alert" v-if="step.done">
                                            👏{{ $t(step.doneTip) }}
                                        </div>
                                        -->

                                        <component
                                            v-if="step.componentName"
                                            :is="step.componentName"
                                            :category="category"
                                            :step="step"
                                            @video="showVideoPopup"
                                            @update="updateStateStepsStatus([step.key])"
                                            @next="gotoStep"
                                            @hide="hideGuidePopover"
                                            @trackEvent="trackEvent"
                                        ></component>
                                    </pt-collapse-item>
                                </pt-collapse>
                            </div>
                        </pt-collapse-item>
                    </pt-collapse>
                </pt-scrollbar>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import GuideStep11 from './GuideStep1-1.vue';
import GuideStep12 from './GuideStep1-2.vue';
import GuideStep13 from './GuideStep1-3.vue';
import GuideStep21 from './GuideStep2-1.vue';
import GuideStep22 from './GuideStep2-2.vue';
import GuideStep31 from './GuideStep3-1.vue';
import GuideStep32 from './GuideStep3-2.vue';
import GuideStep33 from './GuideStep3-3.vue';
import GuideStep34 from './GuideStep3-4.vue';
import GuideComplete from './GuideComplete.vue';

export default {
    name: 'GuidePopover',
    components: {
        GuideStep11,
        GuideStep12,
        GuideStep13,
        GuideStep21,
        GuideStep22,
        GuideStep31,
        GuideStep32,
        GuideStep33,
        GuideStep34,
        GuideComplete
    }
};
</script>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useGuideStore } from '@master/store/guide.store';
import useEventBus from '@assets/plugins/eventBus';
const emit = defineEmits(['close']);

// state
const { activeCategoryKey, allStepCompleted, categories, guideClosed, guidePopoverVisible } =
    storeToRefs(useGuideStore());
const {
    updateStateStepsStatus,
    getStepProgress,
    gotoStep,
    trackEvent,
    hideGuidePopover,
    showVideoPopup,
    showHelpPopover
} = useGuideStore();
const { eventBus } = useEventBus();
const guideScroll = ref();

// 仅在所有步骤都已完成且未点击过关闭按钮才显示
const visibleComplete = computed(() => {
    return allStepCompleted.value && !guideClosed.value;
});

onMounted(() => {
    eventBus.$on('startGuideScroll', handleScroll, false);
});

onUnmounted(() => {
    eventBus.$off('startGuideScroll', handleScroll, false);
});

function handleCloseClick() {
    hideGuidePopover();

    // 如果由Help弹框中调起，关闭时自动打开Help
    if (guideClosed.value) {
        showHelpPopover();
    }
}

function handleScroll(position?: 'top' | 'middle' | 'bottom') {
    guideScroll.value?.scrollToPos(position);
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

// 14px: guide icon margin bottom
// 44px guide icon height
// 60px page top menu height
// 20px guide margin top
$guide-height: calc(100vh - 14px - #{$page-help-icon-margin-bottom} - 44px - 60px - 20px);

// 100px: guide header height
// 16px: guide margin top
$guide-content-height: calc(
    100vh - 14px - #{$page-help-icon-margin-bottom} - 44px - 60px - 20px - 180px - (16px * 2)
);

.guide {
    width: 400px;
    max-height: 600px;
    height: $guide-height;
    padding: 16px 0;
    position: absolute;
    bottom: 100%;
    right: calc(-#{$page-help-icon-width} + -20px);
    margin-bottom: 14px;
    background: $pt-white;
    box-shadow: 0px 8px 30px 4px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    transform-origin: right bottom;
    transition: width 200ms ease 0s, height 200ms ease 0s, max-height 200ms ease 0s,
        transform 300ms cubic-bezier(0, 1.2, 1, 1) 0s, opacity 83ms ease-out 0s;
    display: flex;
    flex-direction: column;

    &.show {
        opacity: 1;
    }

    &.hide {
        transform: scale(0);
        opacity: 0;
    }

    .close {
        cursor: pointer;
        position: absolute;
        top: 18px;
        right: 16px;
        z-index: 100;
        width: 24px;
        height: 24px;
        fill: $pt-black-90;

        &:hover {
            fill: $pt-black-300;
        }
    }

    &::after {
        position: absolute;
        top: 100%;
        right: 60px;
        width: 0;
        height: 0;
        overflow: hidden;
        content: '';
        border-color: $pt-white transparent transparent transparent;
        border-style: solid dashed dashed dashed;
        border-width: 6px;
    }

    > header {
        position: relative;
        text-align: center;
        padding: 24px 0;

        h2 {
            font-weight: 700;
            font-size: 20px;
            line-height: 150%;
            color: $pt-black-900;
            margin-bottom: 4px;
        }

        p {
            font-weight: 400;
            font-size: 13px;
            line-height: 19px;
            color: $pt-black-600;
        }
    }

    :global {
        // 滚动条
        .thumb {
            width: 4px !important;
        }

        .pt-collapse[data-label='collapse'] {
            border: none;
            margin: 0 12px;

            > .pt-collapse-item {
                border: 1px solid $pt-black-40;
                border-radius: 8px;
                margin-bottom: 8px;
                overflow: hidden;

                &[data-percentage='0'] {
                    .pt-progress {
                        display: none;
                    }
                }

                &.is-active {
                    border-radius: 8px;
                    box-shadow: 0px 2px 4px rgba(9, 30, 66, 0.05);
                    .pt-progress {
                        display: block;
                    }
                }

                > div[role='tab'] {
                    > .pt-collapse-item__header {
                        height: 60px;
                        line-height: 60px;
                        border: none;
                        position: relative;

                        &:hover,
                        &.is-active {
                            background-color: $pt-black-10;
                        }

                        > span {
                            font-weight: 700;
                            font-size: 14px;
                            color: $pt-black-600;
                            margin-left: 16px;
                        }

                        .pt-progress {
                            position: absolute;
                            bottom: 0;
                            width: 100%;
                        }
                    }
                }

                > .pt-collapse-item__wrap .pt-collapse-item__content {
                    padding: 0;
                }
            }
        }

        .pt-collapse-item__arrow {
            width: 12px !important;
            height: 12px !important;
            fill: $pt-black-90;
            margin-right: 16px;
        }
        .pt-a-link {
            text-decoration: underline;
            cursor: pointer;
            &:hover {
                color: $pt-black-900;
            }
        }
        .readonly {
            .pt-a-link {
                text-decoration: none;
                cursor: text;
                &:hover {
                    color: inherit;
                }
            }
        }
    }

    .container {
        height: 1px; // 1px是由于 scrollbar 组件依赖此属性; 具体高度值还是由flex计算
        flex: 1;
    }
}

.step {
    margin: 0 16px;

    :global {
        .pt-collapse {
            border: none;
            padding: 6px 0 20px;
        }

        .pt-collapse-item {
            &:not(:last-of-type) {
                margin: 8px 0 10px;
            }

            &:last-of-type {
                .pt-collapse-item__header {
                    border: none;
                }
            }
        }

        .pt-collapse-item__wrap {
            padding: 2px 0 6px;
            border: none;
        }

        .pt-collapse-item__header {
            border: none;
            height: 30px;
            line-height: 30px;
            border-radius: 3px;
            padding: 0 10px;

            &:hover {
                background: $pt-black-25;
            }

            &.is-active {
                // 未完成步骤展开时，icon变成蓝色
                & > div:first-of-type {
                    opacity: 1;
                    border-color: $pt-info;

                    span {
                        color: $pt-info;
                    }
                }
            }
        }

        .pt-collapse-item {
            h3 {
                font-weight: 500;
                font-size: 13px;
                line-height: 20px;
                color: $pt-black-900;
                flex: 1;
            }

            &[data-done='true'] {
                .pt-collapse-item__header {
                    h3 {
                        //opacity: 0.5;
                    }
                }
            }
        }

        .pt-collapse-item__content {
            margin: 0 10px 0 20px;

            > p {
                font-weight: 400;
                font-size: 12px;
                line-height: 16px;
                color: $pt-black-300;
            }

            > button {
                margin-top: 20px;
            }

            > img {
                width: 100%;
                max-width: 100%;
                margin-top: 8px;
                cursor: pointer;
            }
        }

        .pt-collapse-item__arrow {
            margin-right: 0;
        }
    }

    .order {
        width: 20px;
        height: 20px;
        border: 1px solid $pt-black-600;
        border-radius: 50%;
        margin-right: 10px;
        @extend %flex-center;
        opacity: 0.5;

        &.done {
            opacity: 1;
            border-color: $pt-info;
            background-color: $pt-info;

            svg {
                display: block;
            }

            span {
                display: none;
            }
        }

        svg {
            width: 12px;
            height: 12px;
            fill: $pt-white;
            display: none;
        }

        span {
            font-weight: 400;
            font-size: 12px;
            color: $pt-black-600;
        }
    }

    .alert {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: $pt-info;
        margin-bottom: 12px;
        //white-space: nowrap;
    }

    .icon {
        & ~ svg {
            display: none;
        }

        svg {
            width: 20px;
            height: 20px;
            fill: $pt-black-90;
        }
    }
}

:global {
    .pt-tooltip-wrap {
        position: relative;
        margin-top: 20px;
        display: inline-block;
        // font-size:0 消除display: inline-block的3像素空白
        font-size: 0;
        &.hover {
            .tooltip {
                opacity: 1;
                visibility: visible;
                transform: translateY(calc(-100% - 8px));
            }
        }
        .tooltip {
            position: absolute;
            padding: 8px;
            background-color: $pt-black;
            border-radius: 3px;
            font-size: 12px;
            color: $pt-white;
            line-height: 16px;
            left: 0;
            width: 298px;
            transform: translateY(calc(-100% - 8px));
            opacity: 0;
            visibility: hidden;
            transition: 0.2s linear;
            i {
                width: 100%;
                @include line-clamp(3);
            }
            &:before {
                @include pt-triangle('bottom', 6px, $pt-black);
                position: absolute;
                bottom: -12px;
            }
        }
    }
}
</style>
